import { i18n } from 'i18n';
import {
  PipeId,
  PipeDataDto,
  PourReportDto,
  PourStatus,
  StopeDataForm,
  StopeDataDto,
  StopeLocationDto,
  StopePipeArray,
  UpdateStopeDataForm
} from 'providers/api';

export const downloadJsonData = (data: unknown, fileName: string) => {
  const blob = new Blob([JSON.stringify(data)], { type: 'application/json' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};

export const escapeCsvValue = (value: unknown): string => {
  if (value === null || value === undefined) {
    return '';
  }

  const asString = String(value);
  if (!/[",\r\n]/.test(asString)) {
    return asString;
  }

  return `"${asString.replace(/"/g, '""')}"`;
};

const toLookupKey = (value: string): string => value.trim().toLowerCase();

const isBlank = (value: unknown): boolean => {
  if (value === null || value === undefined) {
    return true;
  }

  if (typeof value === 'string') {
    return value.trim() === '';
  }

  return false;
};

const parseOptionalNumber = (value: unknown, context: string): number | undefined => {
  if (isBlank(value)) {
    return undefined;
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  const parsed = Number(String(value).trim());
  if (Number.isFinite(parsed)) {
    return parsed;
  }

  throw new Error(i18n._('Invalid number for {context}: {value}', {
    context,
    value: String(value),
  }));
};

const getFiniteNumber = (value: number | undefined): number | undefined => (
  typeof value === 'number' && Number.isFinite(value) ? value : undefined
);

const roundForCsv = (value: number | undefined): number | '' => (
  value === undefined ? '' : Math.round((value + Number.EPSILON) * 100) / 100
);

const binderTailingsRatioToPercentage = (ratio: number | undefined): number | undefined => (
  ratio === undefined ? undefined : (ratio / (1 + ratio)) * 100
);

interface StopePourExportSummary {
  binderPercentage?: number;
  massConcentration?: number;
  binderTonnes?: number;
}

interface StopePourExportAccumulator {
  binderPercentageTotal: number;
  binderPercentageWeight: number;
  massConcentrationTotal: number;
  massConcentrationWeight: number;
  binderTonnesTotal: number;
  hasBinderTonnes: boolean;
}

const resolvePourWeight = (pour: PourReportDto): number => {
  const volume = getFiniteNumber(pour.pourData?.volume) ?? getFiniteNumber(pour.volume);
  return volume && volume > 0 ? volume : 1;
};

const resolveBinderPercentage = (pour: PourReportDto): number | undefined => (
  getFiniteNumber(pour.pourData?.binderContent)
  ?? binderTailingsRatioToPercentage(getFiniteNumber(pour.pourData?.binderTailingsRatio))
  ?? getFiniteNumber(pour.pasteSelectedSpecification?.binderContent)
  ?? binderTailingsRatioToPercentage(getFiniteNumber(pour.hydraulicSpecification?.binderTailingsRatio))
);

const resolveMassConcentration = (pour: PourReportDto): number | undefined => (
  getFiniteNumber(pour.pourData?.massConcentration)
  ?? getFiniteNumber(pour.pasteSelectedSpecification?.massConcentration)
  ?? getFiniteNumber(pour.hydraulicSelectedSpecification?.massConcentration)
);

const buildStopePourExportSummaryByStopeId = (pours: PourReportDto[]): Map<string, StopePourExportSummary> => {
  const accumulatorsByStopeId = new Map<string, StopePourExportAccumulator>();

  pours.forEach((pour) => {
    if (!pour.stopeId || pour.status === PourStatus.Abandoned || pour.status === PourStatus.Rejected) {
      return;
    }

    const weight = resolvePourWeight(pour);
    const binderPercentage = resolveBinderPercentage(pour);
    const massConcentration = resolveMassConcentration(pour);
    const binderTonnes = getFiniteNumber(pour.pourData?.totalBinder);
    const accumulator = accumulatorsByStopeId.get(pour.stopeId) ?? {
      binderPercentageTotal: 0,
      binderPercentageWeight: 0,
      massConcentrationTotal: 0,
      massConcentrationWeight: 0,
      binderTonnesTotal: 0,
      hasBinderTonnes: false,
    };

    if (binderPercentage !== undefined) {
      accumulator.binderPercentageTotal += binderPercentage * weight;
      accumulator.binderPercentageWeight += weight;
    }

    if (massConcentration !== undefined) {
      accumulator.massConcentrationTotal += massConcentration * weight;
      accumulator.massConcentrationWeight += weight;
    }

    if (binderTonnes !== undefined) {
      accumulator.binderTonnesTotal += binderTonnes;
      accumulator.hasBinderTonnes = true;
    }

    accumulatorsByStopeId.set(pour.stopeId, accumulator);
  });

  return new Map(Array.from(accumulatorsByStopeId.entries()).map(([stopeId, accumulator]) => [
    stopeId,
    {
      binderPercentage: accumulator.binderPercentageWeight > 0
        ? accumulator.binderPercentageTotal / accumulator.binderPercentageWeight
        : undefined,
      massConcentration: accumulator.massConcentrationWeight > 0
        ? accumulator.massConcentrationTotal / accumulator.massConcentrationWeight
        : undefined,
      binderTonnes: accumulator.hasBinderTonnes ? accumulator.binderTonnesTotal : undefined,
    },
  ]));
};

const createCaseInsensitiveRowAccessor = (row: Record<string, unknown>) => {
  const valueByLookupKey = new Map<string, unknown>();

  Object.entries(row).forEach(([key, value]) => {
    valueByLookupKey.set(toLookupKey(key), value);
  });

  const getRaw = (aliases: string[]): unknown => aliases
    .map((alias) => valueByLookupKey.get(toLookupKey(alias)))
    .find((value) => value !== undefined);

  const getString = (aliases: string[]): string | undefined => {
    const raw = getRaw(aliases);
    if (isBlank(raw)) {
      return undefined;
    }
    return String(raw).trim();
  };

  return { getRaw, getString };
};

const getPipeIdValue = (pipeArray?: StopePipeArray): string => {
  const value = pipeArray?.pipeId as PipeId | string | undefined;
  if (!value) {
    return '';
  }
  if (typeof value === 'string') {
    return value;
  }
  return value.value ?? '';
};

interface PipeLookups {
  pipeIdByLookupKey: Map<string, string>;
  pipeNameToIdsByLookupKey: Map<string, string[]>;
}

const buildPipeLookups = (pipeData: PipeDataDto[]): PipeLookups => {
  const pipeIdByLookupKey = new Map<string, string>();
  const pipeNameToIdsByLookupKey = new Map<string, string[]>();

  (pipeData ?? []).forEach((pipe) => {
    if (!pipe?.pipeId) {
      return;
    }

    const { pipeId } = pipe;
    const pipeName = pipe.name ?? pipeId;
    pipeIdByLookupKey.set(toLookupKey(pipeId), pipeId);

    const nameKey = toLookupKey(pipeName);
    const existing = pipeNameToIdsByLookupKey.get(nameKey) ?? [];
    if (!existing.includes(pipeId)) {
      pipeNameToIdsByLookupKey.set(nameKey, [...existing, pipeId]);
    }
  });

  return { pipeIdByLookupKey, pipeNameToIdsByLookupKey };
};

const resolvePipeId = (input: string, stopeId: string, lookups: PipeLookups): string => {
  const key = toLookupKey(input);
  const directId = lookups.pipeIdByLookupKey.get(key);
  if (directId) {
    return directId;
  }

  const byName = lookups.pipeNameToIdsByLookupKey.get(key) ?? [];
  if (byName.length === 1) {
    return byName[0];
  }
  if (byName.length > 1) {
    throw new Error(i18n._('Stope {stopeId} pipe {input} matches multiple pipe IDs. Use a unique pipe name.', {
      stopeId,
      input,
    }));
  }
  throw new Error(i18n._('Stope {stopeId} references unknown pipe {input}.', {
    stopeId,
    input,
  }));
};

interface PourPresetLookups {
  groupIdByLookupKey: Map<string, string>;
  groupNameToIdsByLookupKey: Map<string, string[]>;
}

const buildPourPresetLookups = (groups: { entityId: string; name?: string | null }[]): PourPresetLookups => {
  const groupIdByLookupKey = new Map<string, string>();
  const groupNameToIdsByLookupKey = new Map<string, string[]>();

  groups.forEach((group) => {
    if (!group?.entityId) {
      return;
    }

    const groupId = group.entityId;
    const groupName = group.name ?? groupId;
    groupIdByLookupKey.set(toLookupKey(groupId), groupId);

    const nameKey = toLookupKey(groupName);
    const existing = groupNameToIdsByLookupKey.get(nameKey) ?? [];
    if (!existing.includes(groupId)) {
      groupNameToIdsByLookupKey.set(nameKey, [...existing, groupId]);
    }
  });

  return { groupIdByLookupKey, groupNameToIdsByLookupKey };
};

const resolvePourPresetGroupId = (input: string, stopeId: string, lookups: PourPresetLookups): string => {
  const key = toLookupKey(input);
  const directId = lookups.groupIdByLookupKey.get(key);
  if (directId) {
    return directId;
  }

  const byName = lookups.groupNameToIdsByLookupKey.get(key) ?? [];
  if (byName.length === 1) {
    return byName[0];
  }
  if (byName.length > 1) {
    throw new Error(i18n._('Stope {stopeId} pour preset group {input} matches multiple groups. Use a unique group name.', {
      stopeId,
      input,
    }));
  }
  throw new Error(i18n._('Stope {stopeId} references unknown pour preset group {input}.', {
    stopeId,
    input,
  }));
};

const normalisePipeArrays = (pipeArrays?: StopePipeArray[]): StopePipeArray[] => (pipeArrays ?? [])
  .map((pipeArray) => ({
    pipeId: { value: getPipeIdValue(pipeArray) } as PipeId,
    pipeHorizontalLength: pipeArray.pipeHorizontalLength ?? 0,
    pipeVerticalLength: pipeArray.pipeVerticalLength ?? 0,
  }));

const parsePipeArraysFromCsvRow = (
  row: Record<string, unknown>,
  stopeId: string,
  existingPipeArrays: StopePipeArray[],
  lookups: PipeLookups,
): StopePipeArray[] => {
  const valuesByIndex = new Map<number, { nameOrId?: string; vertical?: unknown; horizontal?: unknown }>();
  let hasPipeData = false;

  Object.entries(row).forEach(([column, rawValue]) => {
    const key = toLookupKey(column);

    const dynamic = key.match(/^pipe_(\d+)_(name|id|pipe_id|vertical_length|horizontal_length)$/);
    if (dynamic) {
      const index = Math.max(0, Number(dynamic[1]) - 1);
      const type = dynamic[2];
      const current = valuesByIndex.get(index) ?? {};
      if (!isBlank(rawValue)) {
        hasPipeData = true;
      }
      if (type === 'name' || type === 'id' || type === 'pipe_id') {
        current.nameOrId = isBlank(rawValue) ? undefined : String(rawValue).trim();
      } else if (type === 'vertical_length') {
        current.vertical = rawValue;
      } else if (type === 'horizontal_length') {
        current.horizontal = rawValue;
      }
      valuesByIndex.set(index, current);
      return;
    }

    const legacy = key.match(/^pipearrays\.(\d+)\.(pipeid\.value|pipeid|pipeverticallength|pipehorizontallength)$/);
    if (legacy) {
      const index = Math.max(0, Number(legacy[1]));
      const type = legacy[2];
      const current = valuesByIndex.get(index) ?? {};
      if (!isBlank(rawValue)) {
        hasPipeData = true;
      }
      if (type === 'pipeid.value' || type === 'pipeid') {
        current.nameOrId = isBlank(rawValue) ? undefined : String(rawValue).trim();
      } else if (type === 'pipeverticallength') {
        current.vertical = rawValue;
      } else if (type === 'pipehorizontallength') {
        current.horizontal = rawValue;
      }
      valuesByIndex.set(index, current);
    }
  });

  if (!hasPipeData) {
    return existingPipeArrays.length > 0
      ? existingPipeArrays
      : [{
        pipeId: { value: '' } as PipeId,
        pipeVerticalLength: 0,
        pipeHorizontalLength: 0,
      }];
  }

  const parsed: StopePipeArray[] = [];
  Array.from(valuesByIndex.keys())
    .sort((a, b) => a - b)
    .forEach((index) => {
      const current = valuesByIndex.get(index) ?? {};
      const existing = existingPipeArrays[index];
      const hasLengthData = !isBlank(current.vertical) || !isBlank(current.horizontal);
      if (!current.nameOrId) {
        if (hasLengthData) {
          throw new Error(i18n._('Stope {stopeId} pipe {pipeNumber} is missing pipe name.', {
            stopeId,
            pipeNumber: index + 1,
          }));
        }
        return;
      }

      parsed.push({
        pipeId: { value: resolvePipeId(current.nameOrId, stopeId, lookups) } as PipeId,
        pipeVerticalLength:
          parseOptionalNumber(current.vertical, `stope '${stopeId}' pipe_${index + 1}_vertical_length`)
          ?? existing?.pipeVerticalLength
          ?? 0,
        pipeHorizontalLength:
          parseOptionalNumber(current.horizontal, `stope '${stopeId}' pipe_${index + 1}_horizontal_length`)
          ?? existing?.pipeHorizontalLength
          ?? 0,
      });
    });

  if (parsed.length === 0) {
    throw new Error(i18n._('Stope {stopeId} must include at least one pipe.', {
      stopeId,
    }));
  }

  return parsed;
};

export const buildStopeUpdateFormsFromCsv = (
  rows: Record<string, unknown>[],
  stopeLocations: StopeLocationDto[],
  stopes: StopeDataDto[],
  pipeData: PipeDataDto[],
  pourPresetGroups: { entityId: string; name?: string | null }[],
): UpdateStopeDataForm[] => {
  const stopeLocationById = new Map(stopeLocations.map((location) => [location.stopeId, location]));
  const stopeLocationByNodeId = new Map(stopeLocations.map((location) => [location.nodeId, location]));
  const stopesById = new Map(stopes.map((stope) => [stope.stopeId, stope]));
  const pipeLookups = buildPipeLookups(pipeData);
  const presetLookups = buildPourPresetLookups(pourPresetGroups);

  const forms: UpdateStopeDataForm[] = [];

  rows.forEach((row, rowIndex) => {
    const accessor = createCaseInsensitiveRowAccessor(row);
    const stopeIdFromCsv = accessor.getString(['stope_id', 'stopeid']);
    const nodeIdFromCsv = accessor.getString(['node_id', 'nodeid']);

    if (!stopeIdFromCsv && !nodeIdFromCsv) {
      return;
    }

    const stopeLocation = (stopeIdFromCsv && stopeLocationById.get(stopeIdFromCsv))
      || (nodeIdFromCsv && stopeLocationByNodeId.get(nodeIdFromCsv));

    if (!stopeLocation) {
      throw new Error(i18n._('Row {rowNumber}: stope {stopeIdentifier} does not exist.', {
        rowNumber: rowIndex + 2,
        stopeIdentifier: stopeIdFromCsv ?? nodeIdFromCsv,
      }));
    }

    const existingStope = stopesById.get(stopeLocation.stopeId);
    const existingPipeArrays = normalisePipeArrays(existingStope?.pipeArrays);

    const stopeName = accessor.getString(['stope_name', 'stopename'])
      ?? existingStope?.stopeName
      ?? stopeLocation.nodeId;

    const volume = parseOptionalNumber(accessor.getRaw(['volume']), `stope '${stopeLocation.stopeId}' volume`)
      ?? existingStope?.volume
      ?? 0;
    const levelRaw = accessor.getRaw(['level']);
    const areaRaw = accessor.getRaw(['area']);
    const level = levelRaw === undefined ? (existingStope?.level ?? '') : String(levelRaw ?? '');
    const area = areaRaw === undefined ? (existingStope?.area ?? '') : String(areaRaw ?? '');
    const pourPresetRaw = accessor.getString([
      'pour_preset_group_name',
      'pourpresetgroupname',
      'pour_preset_group_id',
      'pourpresetgroupid',
    ]);

    const pourPresetGroupId = (() => {
      if (pourPresetRaw === undefined) {
        return existingStope?.pourPresetGroupId;
      }
      if (pourPresetRaw === '') {
        return undefined;
      }
      return resolvePourPresetGroupId(pourPresetRaw, stopeLocation.stopeId, presetLookups);
    })();

    const pipeArrays = parsePipeArraysFromCsvRow(row, stopeLocation.stopeId, existingPipeArrays, pipeLookups);

    forms.push({
      nodeId: stopeLocation.nodeId,
      stopeDataForm: {
        stopeId: stopeLocation.stopeId,
        stopeName,
        volume,
        level,
        area,
        pourPresetGroupId,
        pipeArrays,
      } as StopeDataForm,
    });
  });

  return forms;
};

export const buildStopeCsvRows = (
  stopeLocations: StopeLocationDto[],
  stopes: StopeDataDto[],
  pipeData: PipeDataDto[],
  pourPresetGroupNameById: Map<string, string>,
  pours: PourReportDto[] = [],
): Record<string, string | number>[] => {
  const stopesById = new Map(stopes.map((stope) => [stope.stopeId, stope]));
  const pipeNameById = new Map(pipeData.map((pipe) => [pipe.pipeId, pipe.name ?? pipe.pipeId]));
  const pourSummaryByStopeId = buildStopePourExportSummaryByStopeId(pours);
  const maxPipeCount = Math.max(1, ...stopes.map((stope) => stope.pipeArrays?.length ?? 0));

  return stopeLocations.map((stopeLocation) => {
    const stopeData = stopesById.get(stopeLocation.stopeId);
    const pourSummary = pourSummaryByStopeId.get(stopeLocation.stopeId);
    const geometryPath = stopeData?.stopeGeometryFile?.path ?? '';
    const geometryDisplay = stopeData?.stopeGeometryFile?.display ?? '';
    const geometryObject = stopeData?.stopeGeometryFile
      ? JSON.stringify(stopeData.stopeGeometryFile)
      : '';

    const row: Record<string, string | number> = {
      stope_id: stopeLocation.stopeId,
      stope_name: stopeData?.stopeName ?? stopeLocation.nodeId,
      node_id: stopeLocation.nodeId,
      volume: stopeData?.volume ?? 0,
      binder_percentage: roundForCsv(pourSummary?.binderPercentage),
      mass_concentration: roundForCsv(pourSummary?.massConcentration),
      binder_tonnes: roundForCsv(pourSummary?.binderTonnes),
      level: stopeData?.level ?? '',
      area: stopeData?.area ?? '',
      stopeGeometryFile: geometryObject,
      'stopeGeometryFile.path': geometryPath,
      'stopeGeometryFile.display': geometryDisplay,
      pour_preset_group_name: stopeData?.pourPresetGroupId
        ? (pourPresetGroupNameById.get(stopeData.pourPresetGroupId) ?? stopeData.pourPresetGroupId)
        : '',
    };

    Array.from({ length: maxPipeCount }, (_, i) => i).forEach((i) => {
      const pipeArray = stopeData?.pipeArrays?.[i];
      const pipeId = getPipeIdValue(pipeArray);
      row[`pipe_${i + 1}_name`] = pipeId ? (pipeNameById.get(pipeId) ?? pipeId) : '';
      row[`pipe_${i + 1}_vertical_length`] = pipeArray?.pipeVerticalLength ?? '';
      row[`pipe_${i + 1}_horizontal_length`] = pipeArray?.pipeHorizontalLength ?? '';
    });

    return row;
  });
};
